import { useAction } from '@reatom/npm-react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Avatar as Avatar_, Tooltip } from '@blade/ui-kit'
import { unsafeUrlDialogEvent } from '@root/entities/deeplinking/unsafe-url-dialog'
import { CountMax3DigitsFormatted } from '@root/shared/core/format'
import { ImagePopover } from '@root/shared/ui'

import type { GithubUserProfile } from '../model/github-user-profile'

export const SocialGithubProfileSummary = ({
  profile,
}: {
  profile: GithubUserProfile
}) => {
  const { t } = useTranslation()
  const url = profile.profileUrl
  const imageURL = profile.avatar_url
  const openUnsafeUrlDialog = useAction(unsafeUrlDialogEvent)

  const handleOpenResource = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()

    openUnsafeUrlDialog([url, 'github'])
  }

  const blogUrl = profile.blog?.toString()

  const handleOpenBlogResource = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()

    if (blogUrl) {
      openUnsafeUrlDialog([blogUrl, 'github'])
    }
  }

  return (
    <Root>
      {imageURL ? (
        <Image href={url} onClick={handleOpenResource}>
          <ImagePopover src={imageURL.toString()} alt={profile.displayName}>
            <Avatar
              src={imageURL.toString()}
              alt={profile.displayName}
              size={96}
            />
          </ImagePopover>
        </Image>
      ) : undefined}

      <Text>
        <ExternalLinkRoot href={url} onClick={handleOpenResource}>
          {profile.displayName}
        </ExternalLinkRoot>
        {'  '}
        <SubTitle href={url} onClick={handleOpenResource}>
          @{profile.login}
        </SubTitle>
        {profile.bio ? <Description>{profile.bio}</Description> : undefined}

        {profile.followers !== undefined ||
        profile.following !== undefined ||
        profile.public_repos !== undefined ? (
          <Stats>
            {profile.followers === undefined ? undefined : (
              <CountStat
                value={profile.followers}
                label={t('common.follower', { count: profile.followers })}
              />
            )}
            {profile.following === undefined ? undefined : (
              <CountStat
                value={profile.following}
                label={t('common.following', { count: profile.following })}
              />
            )}
            {profile.public_repos === undefined ? undefined : (
              <CountStat
                value={profile.public_repos}
                label={t('common.repository', { count: profile.public_repos })}
              />
            )}
          </Stats>
        ) : undefined}

        {profile.location || profile.company || profile.blog ? (
          <Meta>
            {profile.company ? (
              <MetaItem>{profile.company}</MetaItem>
            ) : undefined}
            {profile.location ? (
              <MetaItem>{profile.location}</MetaItem>
            ) : undefined}
            {blogUrl ? (
              <MetaItemLink href={blogUrl} onClick={handleOpenBlogResource}>
                {blogUrl}
              </MetaItemLink>
            ) : undefined}
          </Meta>
        ) : undefined}
      </Text>
    </Root>
  )
}

const CountStat = ({ value, label }: { value: number; label: string }) => (
  <Tooltip content={value} align="center">
    <Stat>
      <StatValue>
        <CountMax3DigitsFormatted count={value} />
      </StatValue>
      <StatLabel>{label}</StatLabel>
    </Stat>
  </Tooltip>
)

const Root = styled.div`
  display: flow-root;
  padding: 10px;
  background: var(--background-1);
`

const Avatar = styled(Avatar_)`
  display: block;
`
const Text = styled.div`
  > * + * {
    margin-top: 4px;
  }
`
const SubTitle = styled.a`
  color: var(--text-11);
  font-size: 16px;
  font-weight: 400;
  white-space: nowrap;
`
const Description = styled.p`
  color: var(--text-9);
  font-weight: 400;
  font-size: 15px;
`

const Image = styled.a`
  margin: 0 8px 0 0;
  float: left;
`

const ExternalLinkRoot = styled.a`
  color: var(--text-12);

  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`

const Stats = styled.div`
  display: flex;
  flex-wrap: wrap;
  column-gap: 12px;
  row-gap: 4px;
  align-items: center;
  margin-top: 8px;
`

const Stat = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
`
const StatValue = styled.span`
  color: var(--text-12);
  font-size: 14px;
  font-weight: 600;
`
const StatLabel = styled.span`
  color: var(--text-9);
  font-size: 13px;
`

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  margin-top: 6px !important;
`
const MetaItem = styled.span`
  color: var(--text-11);
  font-size: 13px;
`
const MetaItemLink = styled.a`
  all: unset;
  cursor: pointer;
  color: var(--text-11);
  font-size: 13px;
  &:hover {
    text-decoration: underline;
  }
`
